import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Package, Truck, CheckCircle2, Clock, XCircle, MapPin, ArrowRight, Loader2, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { formatCOP } from '@/utils/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ICON = {
  pending: Clock,
  paid: CheckCircle2,
  processing: Package,
  shipped: Truck,
  delivered: CheckCircle2,
  cancelled: XCircle,
};

const fmt = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const OrderTracking = () => {
  const { orderNumber } = useParams();
  const [params, setParams] = useSearchParams();
  const [emailInput, setEmailInput] = useState(params.get('email') || '');
  const email = params.get('email') || '';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (mail) => {
    if (!mail) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/tracking/order`, {
        params: { number: orderNumber, email: mail },
      });
      setOrder(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'No pudimos encontrar tu pedido con esos datos.');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => { load(email); }, [load, email]);

  const submitEmail = (e) => {
    e.preventDefault();
    setParams({ email: emailInput.trim() });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12">
      <div className="mb-6">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Seguimiento</p>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="tracking-title">
          Pedido {orderNumber}
        </h1>
      </div>

      {/* If email not yet provided, ask for it */}
      {!email && (
        <form onSubmit={submitEmail} className="bg-white rounded-3xl border border-[#E7E5E4] p-6 space-y-4" data-testid="tracking-email-form">
          <p className="text-sm text-[#57534E]">Ingresa el correo con el que realizaste el pedido para ver el estado.</p>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Correo</Label>
            <div className="relative mt-1">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
              <Input
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="tu@correo.com"
                className="pl-10"
                data-testid="tracking-email-input"
              />
            </div>
          </div>
          <Button type="submit" className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6" data-testid="tracking-email-submit">
            Ver mi pedido <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      )}

      {loading && (
        <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" /></div>
      )}

      {error && !loading && email && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center" data-testid="tracking-error">
          <p className="text-red-800 font-medium mb-3">{error}</p>
          <Button
            onClick={() => setParams({})}
            variant="outline"
            className="rounded-full border-red-300 text-red-700"
          >
            Intentar con otro correo
          </Button>
        </div>
      )}

      {order && !loading && (
        <div className="space-y-6" data-testid="tracking-result">
          {/* Timeline */}
          <div className="bg-white rounded-3xl border border-[#E7E5E4] p-6">
            <h2 className="font-display font-semibold text-lg text-[#1C1917] mb-6">Estado del pedido</h2>
            <div className="relative">
              {order.timeline.map((step, i) => {
                const Icon = STATUS_ICON[step.key] || Package;
                const isLast = i === order.timeline.length - 1;
                return (
                  <div key={step.key} className="flex gap-4 pb-6 last:pb-0" data-testid={`tracking-step-${step.key}`}>
                    <div className="relative flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        step.active
                          ? 'bg-[#2A5C43] text-white ring-4 ring-[#F7F2E8]'
                          : step.reached
                            ? 'bg-[#F7F2E8] text-[#2A5C43]'
                            : 'bg-[#F5F5F4] text-[#8BA58F]'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 flex-1 mt-1 ${step.reached ? 'bg-[#2A5C43]' : 'bg-[#F5F5F4]'}`} style={{ minHeight: '32px' }} />
                      )}
                    </div>
                    <div className="flex-1 pt-1">
                      <p className={`font-semibold ${step.reached ? 'text-[#1C1917]' : 'text-[#8BA58F]'}`}>{step.label}</p>
                      {step.timestamp && <p className="text-xs text-[#57534E] mt-0.5">{fmt(step.timestamp)}</p>}
                      {step.key === 'shipped' && order.tracking_number && step.reached && (
                        <p className="text-xs text-[#2A5C43] font-medium mt-1" data-testid="tracking-guide">
                          Guía: <span className="font-bold">{order.tracking_number}</span>
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-3xl border border-[#E7E5E4] p-6">
            <h2 className="font-display font-semibold text-lg text-[#1C1917] mb-4">Productos</h2>
            <div className="divide-y divide-[#F5F5F4]">
              {order.items.map((it, i) => (
                <div key={i} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#1C1917]">{it.product_name}</p>
                    <p className="text-xs text-[#57534E]">{it.size} • SKU {it.sku} • x{it.quantity}</p>
                  </div>
                  <p className="font-display font-bold text-[#1C1917]">{formatCOP(it.subtotal)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#E7E5E4] mt-4 pt-4 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-[#57534E]">Subtotal</span><span>{formatCOP(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#57534E]">Envío</span><span>{formatCOP(order.shipping_cost)}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-[#2A5C43]"><span>Descuento</span><span>-{formatCOP(order.discount)}</span></div>}
              <div className="flex justify-between pt-2 border-t border-[#E7E5E4] font-display font-bold text-lg text-[#1C1917]">
                <span>Total</span><span>{formatCOP(order.total)}</span>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-3xl border border-[#E7E5E4] p-6">
            <h2 className="font-display font-semibold text-lg text-[#1C1917] mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#D47A4A]" /> Dirección de envío
            </h2>
            <p className="text-sm text-[#1C1917]">Para: <b>{order.customer_name}</b></p>
            <p className="text-sm text-[#57534E]">{order.shipping_neighborhood ? order.shipping_neighborhood + ', ' : ''}{order.shipping_city}, {order.shipping_department}</p>
          </div>

          {order.status === 'delivered' && (
            <div className="bg-gradient-to-br from-[#F7F2E8] to-[#FFF7EE] rounded-3xl border border-[#E7E5E4] p-6 text-center">
              <p className="font-display font-semibold text-lg text-[#1C1917] mb-2">¿Cómo fue tu experiencia?</p>
              <p className="text-sm text-[#57534E] mb-4">Cuéntanos qué tal — nos ayudas a mejorar y a otros clientes a elegir.</p>
              <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
                <Link to={`/resena/${order.order_number}?email=${encodeURIComponent(email)}`} data-testid="tracking-review-link">
                  Dejar reseña <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderTracking;
